'use client';

import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import ArrowSVG from '../../public/img/angle-right.svg';
import CheckSVG from '../../public/img/check-red.svg';
import { H1 } from '../styled/typography';
import { abcRuArray } from '../../lib/consts';
import { Specialization } from '../../models/specialization';
import { PersonInListV3 } from '../../models/person';

const mainList = [
  { id: 1, page: 'speciality', title: 'Специализации', subtitle: 'Я ищу' },
  { id: 2, page: 'alphabet', title: 'По алфавиту' },
];

const StyledCheckbox = styled.div`
  width: 20px;
  height: 20px;
  border: 1px solid #bfc1c3;

  &.active {
    border-color: #f23d32;
    background-color: #f23d32;
    background-repeat: no-repeat;
    background-image: url("data:image/svg+xml,<svg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M1 3.5L4.21429 7L11 1' stroke='white' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>");
    background-size: 70%;
    background-position: 50%;
    background-origin: padding-box;
    background-clip: border-box;
  }
`;

type IPersonFilterProps = {
  query: {
    last_name__startswith: string | [];
    pages: number[];
    prof: Specialization[];
  };
  specializationsList: Specialization[];
  persons: {
    results: PersonInListV3[];
    next: string | null;
    count?: number;
  };
  onSelect: ({
    profs,
    letter,
    close,
  }: {
    profs: Specialization[];
    letter: string | [];
    close: boolean;
  }) => void;
};

export function PersonFilter({
  query,
  specializationsList,
  persons,
  onSelect,
}: IPersonFilterProps) {
  const [filterPage, setFilterPage] = useState('');
  const [specialities, setSpecialities] = useState<number[]>([]);
  const [allSpecialities, setAllSpecialities] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState<string | []>('');

  useEffect(() => {
    const { last_name__startswith: letter, prof } = query;
    setSelectedLetter(letter);
    setSpecialities([...prof.map((p) => p.id)]);
  }, [query]);

  const selectSpeciality = (id: number) => {
    const copy = [...specialities];

    if (copy.includes(id)) {
      const index = copy.indexOf(id);
      copy.splice(index, 1);
    } else {
      copy.push(id);
    }

    if (copy.length === specializationsList.length) {
      setAllSpecialities(true);
    } else {
      setAllSpecialities(false);
    }

    setSpecialities(copy);
  };

  const selectAllSpecialities = () => {
    if (!allSpecialities) {
      setSpecialities(specializationsList.map((s) => s.id));
    } else {
      setSpecialities([]);
    }
    setAllSpecialities((prevVal) => !prevVal);
  };

  const showPersonsCount = () => {
    const selected = specializationsList.filter((s) =>
      specialities.includes(s.id)
    );

    if (!selected.length || selected.length === specializationsList.length) {
      return persons.count;
    }

    return selected.reduce((acc, current) => acc + current.person_count, 0);
  };

  const showSelected = () => {
    const selected = specializationsList.filter((s) =>
      specialities.includes(s.id)
    );
    onSelect({ profs: selected, letter: selectedLetter, close: true });
  };

  const reset = () => {
    onSelect({ profs: [], letter: [], close: false });
  };

  return (
    <div css={{ padding: '0 8px' }}>
      {!filterPage && (
        <>
          <H1 css={{ margin: '12px 16px' }}>Фильтры</H1>

          <div>
            {mainList.map((item) => (
              <button
                type='button'
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  gap: 5,
                  height: 75,
                  background: '#fff',
                  padding: 14,
                  boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.05)',
                  marginBottom: 1,
                }}
                key={item.id}
                onClick={() => setFilterPage(item.page)}
              >
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                  }}
                >
                  <strong css={{ fontSize: 16 }}>{item.title}</strong>
                  <small css={{ fontSize: 12 }}>{item.subtitle}</small>
                </div>
                <ArrowSVG css={{ fill: '#C7C7CC' }} />
              </button>
            ))}
          </div>
        </>
      )}

      {filterPage === 'speciality' && (
        <>
          <H1 css={{ margin: '12px 16px' }}>Специализации</H1>

          <div>
            <button
              type='button'
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                padding: '12px 18px',
                background: '#fff',
              }}
              onClick={selectAllSpecialities}
            >
              <div>
                <strong
                  css={{ fontSize: 16, fontWeight: 700, color: '#393939' }}
                >
                  Все
                </strong>
                <small
                  css={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#BFC1C3',
                    marginLeft: 14,
                  }}
                >
                  {persons.count}
                </small>
              </div>
              <StyledCheckbox className={allSpecialities ? 'active' : ''} />
            </button>
            {specializationsList.map((specialization) => (
              <button
                type='button'
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '12px 18px',
                  background: '#fff',
                }}
                key={specialization.id}
                onClick={() => selectSpeciality(specialization.id)}
              >
                <div>
                  <strong
                    css={{ fontSize: 16, fontWeight: 700, color: '#393939' }}
                  >
                    {specialization.title}
                  </strong>
                  <small
                    css={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: '#BFC1C3',
                      marginLeft: 14,
                    }}
                  >
                    {specialization.person_count}
                  </small>
                </div>
                <StyledCheckbox
                  className={
                    specialities.includes(specialization.id) ? 'active' : ''
                  }
                />
              </button>
            ))}
          </div>
        </>
      )}

      {filterPage === 'alphabet' && (
        <>
          <H1 css={{ margin: '12px 16px' }}>По алфавиту</H1>

          <div>
            {abcRuArray.map((letter) => (
              <button
                type='button'
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '18px 20px',
                  background: '#fff',
                  boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.05)',
                  marginBottom: 1,
                }}
                key={letter}
                onClick={() => setSelectedLetter(letter)}
              >
                <strong css={{ fontSize: 16, color: '#393939' }}>
                  {letter}
                </strong>
                {selectedLetter === letter && <CheckSVG />}
              </button>
            ))}
          </div>
        </>
      )}

      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 26,
          marginTop: 70,
          marginBottom: 16,
        }}
      >
        <button
          type='button'
          css={{
            maxWidth: 300,
            fontFamily: 'Golos',
            width: '100%',
            background: '#ff4a2e',
            backgroundImage:
              'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
            backgroundSize: '500% 500%',
            color: '#fff',
            padding: '6px',
            fontSize: 16,
            fontWeight: 500,
            borderRadius: '1.28em',
          }}
          onClick={showSelected}
        >
          Показать {showPersonsCount()} персоны
        </button>
        <button type='button' css={{ color: '#f44336' }} onClick={reset}>
          Сбросить
        </button>
      </div>
    </div>
  );
}
